// src/features/business-functions/pages/BusinessFunctionWorkspacePage.jsx
import React, { Suspense, lazy, useState } from 'react';
import { Box, Fade, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import DynamicBreadcrumbs from '../../../components/Shared/Breadcrumb/index.tsx';
import { createBreadcrumbNavigateHandler } from '../../../config/navigation/breadcrumbs';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider';
import useBusinessFunctionsSx from '../hooks/useBusinessFunctionsSx';
import useWorkspaceDetail from '../hooks/useWorkspaceDetail';
import useWorkspaceTableTabs from '../hooks/useWorkspaceTableTabs';
import useCreateWorkspaceTable from '../hooks/useCreateWorkspaceTable';
import useCanCreateWorkspaceTable from '../hooks/useCanCreateWorkspaceTable';
import useWorkspaceBreadcrumbs from '../hooks/useWorkspaceBreadcrumbs';
import WorkspaceHeader from '../components/workspace-detail/WorkspaceHeader';
import WorkspaceTabBarSkeleton from '../components/workspace-detail/WorkspaceTabBarSkeleton';
import AccountTabBar from '../components/AccountTabBar';
import WorkspaceZeroState from '../components/workspace-detail/WorkspaceZeroState';

const TableRecordsPanel = lazy(
  () => import('../components/workspace-detail/tables/TableRecordsPanel')
);

function TableRecordsFallback() {
  return <Box sx={{ mt: 2.5, minHeight: 280 }} aria-hidden="true" />;
}

/**
 * Workspace detail — each lead form renders as a top-level tab with its records.
 */
function BusinessFunctionWorkspacePageContent() {
  const styles = useBusinessFunctionsSx();
  const navigate = useNavigate();
  const [paginationContainer, setPaginationContainer] = useState(null);

  const {
    workspace,
    tables,
    hasTables,
    isLoadingWorkspace,
    error,
    workspaceId,
    accountId,
  } = useWorkspaceDetail();

  const canCreateTable = useCanCreateWorkspaceTable(workspaceId, accountId);
  const { createTable } = useCreateWorkspaceTable(
    accountId,
    workspaceId,
    { canCreate: canCreateTable }
  );

  const {
    tabConfig,
    activeTableId,
    activeTable,
    handleTabChange,
    hasTableTabs,
  } = useWorkspaceTableTabs(tables);

  const breadcrumbsData = useWorkspaceBreadcrumbs(
    accountId,
    workspace?.name
  );

  const handleCreateClick = (type) => {
    if (type === 'table' || !type) {
      createTable();
    }
  };

  const showTabBarRegion = Boolean(workspaceId) && (isLoadingWorkspace || hasTableTabs);

  return (
    <Box sx={styles.pageRoot}>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={createBreadcrumbNavigateHandler(navigate)}
      />

      <WorkspaceHeader
        workspace={workspace}
        styles={styles}
        isLoading={isLoadingWorkspace}
        onCreateClick={handleCreateClick}
        canCreateTable={canCreateTable}
      />

      {error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : null}

      {!isLoadingWorkspace && !hasTables ? (
        <WorkspaceZeroState
          styles={styles}
          onCreateClick={handleCreateClick}
          canCreateTable={canCreateTable}
        />
      ) : null}

      {showTabBarRegion || hasTableTabs ? (
        <Box sx={styles.workspaceTableCard}>
          {showTabBarRegion ? (
            hasTableTabs ? (
              <AccountTabBar
                tabConfig={tabConfig}
                activeTab={activeTableId}
                onTabChange={handleTabChange}
                styles={styles}
              />
            ) : (
              <WorkspaceTabBarSkeleton styles={styles} />
            )
          ) : null}

          <Box sx={styles.tabPanelContainer}>
            {hasTableTabs && activeTable ? (
              <Fade key={activeTable.id} in timeout={200}>
                <Box>
                  <Suspense fallback={<TableRecordsFallback />}>
                    <TableRecordsPanel
                      workspaceId={workspaceId}
                      accountId={accountId}
                      workspaceName={workspace?.name}
                      table={activeTable}
                      styles={styles}
                      paginationContainer={paginationContainer}
                      showHeader={false}
                    />
                  </Suspense>
                </Box>
              </Fade>
            ) : null}
          </Box>
        </Box>
      ) : null}
      {hasTableTabs ? (
        <Box ref={setPaginationContainer} sx={{ width: '100%' }} />
      ) : null}
    </Box>
  );
}

export default function BusinessFunctionWorkspacePage() {
  const { workspaceId } = useParams();

  return (
    <FeatureThemeProvider>
      <BusinessFunctionWorkspacePageContent key={workspaceId} />
    </FeatureThemeProvider>
  );
}
